import React, { useState, useEffect, useRef, useCallback} from 'react';
import { AnimatePresence, color, motion } from 'framer-motion';
import PostForm from './PostForm';
import Editpost from './editform';
import EditArticleForm from './EditArticleForm';
import EditBlogForm from './EditBlogForm';
import HiringForm from './HiringForm';
import LookingForJobForm from './LookingForJobForm';
import ArticleCard, { BLOG_CARD_PROPS } from './shared/ArticleCard';
import HiringCard from './shared/HiringCard';
import LookingForJobCard from './shared/LookingForJobCard';
import QuestionCard from './shared/QuestionCard';

import DeleteButtons from './icon/deleteicon'; 
import ShareButton from './icon/shareicome';
import RepostButton from './icon/repostbutton';
import ReactionIcon from './icon/ReactionIcon';
import ReactionButton from './shared/ReactionButton';
import ReplyButton from './icon/replybutton';
import RepostModal from './shared/RepostModal';
import { Link } from 'react-router-dom';
import PhotoLightbox from './PhotoLightbox';
import { resolveAvatarUrl, handleAvatarError } from '../utils/profilePicUrl';
import { getLikerDisplayName, getAuthorDisplayName, formatAuthorMetaLine } from '../utils/displayName';
import { getMediaMimeType, isMediaImage, isMediaVideo } from '../utils/mediaDetect';
import PostMediaGrid from './shared/PostMediaGrid';
import AuthorNameLink from './shared/AuthorNameLink';
import MentionInput from './shared/MentionInput';
import CaptionAssist, { plainExcerpt } from './shared/CaptionAssist';
import {
  postAuthorStyle,
  postMetaStyle,
  postTitleStyle,
  postTitleLinkStyle,
  postContentStyle,
  postFonts,
} from './shared/postCardStyles';
import { getTopReactions, DEFAULT_REACTION } from '../utils/reactions';
import { recordInteraction } from '../utils/analytics';
import { appendMentionIds, createLinkifiedText, toStoredMentionText } from '../utils/mentions';
import { htmlToExcerpt } from '../utils/sanitizeHtml';
import { isEmployer, canApplyForJobs, isAdmin, canManagePost } from '../utils/userRole';
import SurveyPanel from './survey/SurveyPanel';
import useLiveRefresh from '../hooks/useLiveRefresh';
import { Search, ChevronRight, ChevronUp, Upload, Send, Reply, Pencil, Trash2, Flag, Ban, Bookmark } from 'lucide-react';
import ReportModal from './shared/ReportModal';
import { confirmAndBlock } from '../utils/moderation';
import { setPostSaved } from '../utils/saved';
import './PostList.css';
import './ReplyComposer.css';
import './ReplyThread.css';
import './ActionMenu.css';
import { getChildReplies, MAX_REPLY_INDENT } from '../utils/nestReplies';


const TYPED_FEED_TYPES = ['article', 'blog', 'hiring', 'looking_for_job', 'question'];
const TYPED_PAGE_SIZE = 50;

const appendMentionedField = (formData, fieldName, text, extraIds) => {
  const stored = toStoredMentionText(text ?? '');
  formData.append(fieldName, stored);
  appendMentionIds(formData, stored, extraIds);
  return stored;
};

const PostList = ({
  posts,
  onReply,
  user,
  onRefresh: onRefreshProp,
  onNewReplyCreated,
  preserveOrder = false,
  hidePagination = false,
  compact = false,
}) => {
  const [replyContents, setReplyContents] = useState({});
  const [mediaFiles, setMediaFiles] = useState({});
  const [showReplyBox, setShowReplyBox] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState({});
  const [errors, setErrors] = useState({});
  const [likes, setLikes] = useState({});
  const [userLiked, setUserLiked] = useState({});
  const [userReactions, setUserReactions] = useState({});
  const [showReplies, setShowReplies] = useState({});
  const scrollRef = useRef(null);
const [nestedReplyMedia, setNestedReplyMedia] = useState({});

  const [expandedContent, setExpandedContent] = useState({});
const [showDropdown, setShowDropdown] = useState({});
const [reportTarget, setReportTarget] = useState(null);
const [blockedAuthorIds, setBlockedAuthorIds] = useState(() => new Set());
// Saved state changed in this session, keyed by saved post id; falls back to post.is_saved.
const [savedOverrides, setSavedOverrides] = useState({});
const [showAllImages, setShowAllImages] = useState({}); // key by reply.id
  const [popupPhoto, setPopupPhoto] = useState(null);
  const closePopup = () => setPopupPhoto(null);
  const [isDragging, setIsDragging] = useState({});
  const [openDropdownId, setOpenDropdownId] = useState(null);
const [sortType, setSortType] = useState('latest'); // NEW
const [replies, setReplies] = useState({});
const [searchTerm, setSearchTerm] = useState('ALL');

const [searchQuery, setSearchQuery] = useState('');
 const [selectedCategory, setSelectedCategory] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [hiringInboxCount, setHiringInboxCount] = useState(0);
  const [profileViewCount, setProfileViewCount] = useState(0);
  const [hiringBadgeDismissed, setHiringBadgeDismissed] = useState(false);
  const [profileBadgeDismissed, setProfileBadgeDismissed] = useState(false);
  const prevHiringCountRef = useRef(0);
  const prevProfileCountRef = useRef(0);
  const [showPostForm, setShowPostForm, ] = useState(false);

  const TYPE_TABS = [
    { id: 'all', label: 'All' },
    { id: 'question', label: 'Q&A' },
    { id: 'article', label: 'Articles' },
    { id: 'blog', label: 'Blogs' },
    { id: 'hiring', label: 'Hiring' },
    { id: 'looking_for_job', label: 'Open to work' },
    { id: 'survey', label: 'Survey' },
  ];

  const getEffectivePostType = (post) => {
    if (post?.post_type === 'repost' && post?.original_post) {
      return post.original_post.post_type || 'text';
    }
    return post?.post_type || 'text';
  };

  const isEmployerUser = isEmployer(user);
  const isSeekerUser = canApplyForJobs(user);
  const showEmployerInbox = isEmployerUser && typeFilter === 'hiring';
  const showSeekerInbox = isSeekerUser && typeFilter === 'looking_for_job';
  const inboxCount = showEmployerInbox
    ? hiringInboxCount
    : showSeekerInbox
      ? profileViewCount
      : 0;
  const inboxTo = showEmployerInbox
    ? '/hiring/applications'
    : '/profile-views';
  const inboxTitle = showEmployerInbox
    ? inboxCount > 0
      ? `${inboxCount} new application${inboxCount === 1 ? '' : 's'}`
      : 'Application inbox'
    : inboxCount > 0
      ? `${inboxCount} profile view${inboxCount === 1 ? '' : 's'}`
      : 'Profile views inbox';

  const [editPost, setEditPost] = useState(null);
const [showEditModal, setShowEditModal] = useState(false);
  const [visibleReplies, setVisibleReplies] = useState({});



const [showNestedReplies, setShowNestedReplies] = useState({});




   const [replyingToReplyId, setReplyingToReplyId] = useState(null);
  const [newReplyContent, setNewReplyContent] = useState(''); //replytoreply
const API_URL = process.env.REACT_APP_API_URL; 
const [activeReplyBox, setActiveReplyBox] = useState(null);
const [nestedReplyText, setNestedReplyText] = useState({});
const [whoLiked, setWhoLiked] = useState({}); // NEW: store who liked each post
const [showLikesModal, setShowLikesModal] = useState(null); // NEW: null or target_key
const [showLikesTooltip, setShowLikesTooltip] = useState(null); // Tooltip showing who liked
const [expandedPostId, setExpandedPostId] = useState(null);
const [reposts, setReposts] = useState({});
const [userReposted, setUserReposted] = useState({});
const [userCaptions, setUserCaptions] = useState({});
const [repostModalPost, setRepostModalPost] = useState(null);
const [repostSubmitting, setRepostSubmitting] = useState(false);
  const [typedPosts, setTypedPosts] = useState([]);
  const [typedLoading, setTypedLoading] = useState(false);
  const [typedHasMore, setTypedHasMore] = useState(false);
  const [typedOffset, setTypedOffset] = useState(0);
  const typedRequestIdRef = useRef(0);
  const isTypedFilter = TYPED_FEED_TYPES.includes(typeFilter);

  const loadTypedPosts = useCallback(async ({ append = false, offset = 0 } = {}) => {
    if (!API_URL || !TYPED_FEED_TYPES.includes(typeFilter)) {
      return;
    }
    const requestId = typedRequestIdRef.current + 1;
    typedRequestIdRef.current = requestId;
    setTypedLoading(true);
    try {
      const params = new URLSearchParams({
        post_type: typeFilter,
        limit: String(TYPED_PAGE_SIZE),
        offset: String(offset),
      });
      if (user?.id) {
        params.set('viewer_id', String(user.id));
      }
      const res = await fetch(`${API_URL}/get_posts.php?${params.toString()}`);
      const data = await res.json();
      if (requestId !== typedRequestIdRef.current) {
        return;
      }
      const incoming = data.success ? (data.posts || []) : [];
      setTypedPosts((prev) => {
        if (!append) {
          return incoming;
        }
        const seen = new Set(prev.map((post) => post.id));
        const merged = [...prev];
        incoming.forEach((post) => {
          if (!seen.has(post.id)) {
            merged.push(post);
            seen.add(post.id);
          }
        });
        return merged;
      });
      setTypedOffset(
        typeof data.next_offset === 'number' ? data.next_offset : offset + incoming.length
      );
      setTypedHasMore(Boolean(data.has_more));
    } catch (err) {
      if (requestId !== typedRequestIdRef.current) {
        return;
      }
      if (!append) {
        setTypedPosts([]);
      }
      setTypedHasMore(false);
    } finally {
      if (requestId === typedRequestIdRef.current) {
        setTypedLoading(false);
      }
    }
  }, [API_URL, typeFilter, user?.id]);

  useEffect(() => {
    if (!isTypedFilter) {
      typedRequestIdRef.current += 1;
      setTypedPosts([]);
      setTypedHasMore(false);
      setTypedOffset(0);
      setTypedLoading(false);
      return undefined;
    }
    setTypedPosts([]);
    setTypedOffset(0);
    loadTypedPosts({ append: false, offset: 0 });
    return undefined;
  }, [isTypedFilter, typeFilter, loadTypedPosts]);

  const onRefresh = useCallback((...args) => {
    const result = typeof onRefreshProp === 'function' ? onRefreshProp(...args) : undefined;
    if (TYPED_FEED_TYPES.includes(typeFilter)) {
      loadTypedPosts({ append: false, offset: 0 });
    }
    return result;
  }, [onRefreshProp, typeFilter, loadTypedPosts]);

  // Employer: badge for new hiring applications (Hiring tab)
  useEffect(() => {
    if (!isEmployerUser || !user?.id || !API_URL) {
      setHiringInboxCount(0);
      prevHiringCountRef.current = 0;
      return undefined;
    }

    let cancelled = false;
    const loadInboxCount = async () => {
      try {
        const params = new URLSearchParams({
          employer_id: String(user.id),
          summary_only: '1',
          limit: '1',
        });
        const res = await fetch(
          `${API_URL}/get_job_applications.php?${params.toString()}`
        );
        const data = await res.json();
        if (!cancelled && data.success) {
          const next = Number(data.summary?.submitted) || 0;
          if (next > prevHiringCountRef.current) {
            setHiringBadgeDismissed(false);
          }
          prevHiringCountRef.current = next;
          setHiringInboxCount(next);
        }
      } catch (err) {
        console.error('Hiring inbox count error:', err);
      }
    };

    loadInboxCount();
    const onFocus = () => loadInboxCount();
    window.addEventListener('focus', onFocus);
    const timer = window.setInterval(loadInboxCount, 45000);

    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
      window.clearInterval(timer);
    };
  }, [API_URL, isEmployerUser, user?.id]);

  // Intern/seeker: badge for unread profile views (Open to work tab)
  useEffect(() => {
    if (!isSeekerUser || !user?.id || !API_URL) {
      setProfileViewCount(0);
      prevProfileCountRef.current = 0;
      return undefined;
    }

    let cancelled = false;
    const loadViewCount = async () => {
      try {
        const params = new URLSearchParams({ user_id: String(user.id) });
        const res = await fetch(
          `${API_URL}/get_profile_view_count.php?${params.toString()}`
        );
        const data = await res.json();
        if (!cancelled && data.success) {
          const next = Number(data.unread_count) || 0;
          if (next > prevProfileCountRef.current) {
            setProfileBadgeDismissed(false);
          }
          prevProfileCountRef.current = next;
          setProfileViewCount(next);
        }
      } catch (err) {
        console.error('Profile view count error:', err);
      }
    };

    loadViewCount();
    const onFocus = () => loadViewCount();
    window.addEventListener('focus', onFocus);
    const timer = window.setInterval(loadViewCount, 45000);

    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
      window.clearInterval(timer);
    };
  }, [API_URL, isSeekerUser, user?.id]);

  // Fetch likes from backend when posts or user ID changes
  const fetchLikesAndReposts = useCallback(async () => {
    if (!user?.id || !API_URL) return;
    try {
      const res = await fetch(`${API_URL}/get_likes.php?user_id=${user.id}`);
      const raw = await res.text();
      let data = null;
      try {
        data = JSON.parse(raw);
      } catch {
        console.error("get_likes.php returned non-JSON:", raw);
        return;
      }
      if (data.success) {
        setLikes(data.total_likes || {});
        setUserLiked(data.user_liked || {});
        setUserReactions(data.user_reactions || {});
        setWhoLiked(data.who_liked || {});
      } else {
        console.warn('Failed to load like data');
      }
    } catch (err) {
      console.error('Error fetching likes:', err.message);
    }

    try {
      const res = await fetch(`${API_URL}/get_reposts.php?user_id=${user.id}`);
      const data = await res.json();
      if (data.success) {
        setReposts(data.total_reposts || {});
        setUserReposted(data.user_reposted || {});
        setUserCaptions(data.user_captions || {});
      }
    } catch (err) {
      console.error('Error fetching reposts:', err.message);
    }
  }, [API_URL, user?.id]);

  useEffect(() => {
    fetchLikesAndReposts();
  }, [fetchLikesAndReposts, posts]);

  useLiveRefresh(fetchLikesAndReposts, 12000, Boolean(user?.id)); 











  
// Handles like / reaction / unlike
const handleLike = async (targetId, type = 'post', reactionType = DEFAULT_REACTION) => {
  const key = `${type}_${targetId}`;
  const liked = userLiked[key];
  const currentReaction = userReactions[key] || DEFAULT_REACTION;
  const removing = reactionType === null || (liked && reactionType === currentReaction);
  const changing = liked && !removing && reactionType !== currentReaction;
  const url = removing ? 'unlike.php' : 'like.php';

  try {
    const body = {
      user_id: user.id,
      target_id: targetId,
      target_type: type,
    };
    if (!removing) {
      body.reaction_type = reactionType || DEFAULT_REACTION;
    }

    const res = await fetch(`${API_URL}/${url}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams(body),
    });

    const raw = await res.text();
    let data = null;
    try {
      data = JSON.parse(raw);
    } catch {
      console.error("Like/unlike endpoint returned non-JSON:", raw);
      alert("Server returned invalid response for like action.");
      return;
    }

    if (data.success) {
      const nextReaction = removing ? null : (data.reaction_type || reactionType || DEFAULT_REACTION);

      setLikes((prev) => ({
        ...prev,
        [key]: removing
          ? Math.max(0, (prev[key] || 1) - 1)
          : changing
            ? (prev[key] || 1)
            : (prev[key] || 0) + 1,
      }));

      setUserLiked((prev) => ({
        ...prev,
        [key]: !removing,
      }));

      setUserReactions((prev) => {
        const next = { ...prev };
        if (removing) delete next[key];
        else next[key] = nextReaction;
        return next;
      });

      setWhoLiked((prev) => {
        const currentList = Array.isArray(prev[key]) ? prev[key] : [];
        const entry = {
          user_id: user.id,
          username: user.username,
          first_name: user.first_name || user.firstname || '',
          last_name: user.last_name || user.lastname || '',
          profile_pic: user.profile_pic,
          reaction_type: nextReaction || DEFAULT_REACTION,
        };

        if (removing) {
          return {
            ...prev,
            [key]: currentList.filter((e) => e.user_id !== user.id),
          };
        }

        const idx = currentList.findIndex((e) => e.user_id === user.id);
        if (idx >= 0) {
          const updated = [...currentList];
          updated[idx] = { ...updated[idx], reaction_type: nextReaction };
          return { ...prev, [key]: updated };
        }

        return { ...prev, [key]: [...currentList, entry] };
      });
    } else {
      if (!liked && data.message !== 'Already liked') {
        alert(data.message || 'Like failed');
      }
    }
  } catch (err) {
    console.error('Like/unlike request failed:', err.message);
    alert('Network error: ' + err.message);
  }
};

const openRepostModal = (post) => {
  if (!user?.id) {
    alert('Please log in first to repost.');
    return;
  }
  setRepostModalPost(post);
};

const getRepostTargetId = (post) => {
  if (post?.post_type === 'repost' && post?.original_post?.id) {
    return post.original_post.id;
  }
  return post?.id;
};

const submitRepost = async (caption) => {
  const post = repostModalPost;
  if (!post || !user?.id) return;

  const targetId = getRepostTargetId(post);
  const key = String(targetId);
  const already = !!userReposted[key];

  setRepostSubmitting(true);
  try {
    const res = await fetch(`${API_URL}/repost.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        user_id: user.id,
        post_id: targetId,
        action: 'repost',
        caption: caption || '',
      }),
    });

    const data = await res.json();
    if (data.success) {
      setReposts((prev) => ({
        ...prev,
        [key]: typeof data.repost_count === 'number'
          ? data.repost_count
          : Math.max(0, (prev[key] || 0) + (already ? 0 : 1)),
      }));
      setUserReposted((prev) => ({
        ...prev,
        [key]: true,
      }));
      setUserCaptions((prev) => ({
        ...prev,
        [key]: data.caption ?? caption ?? '',
      }));
      setRepostModalPost(null);
      if (typeof onRefresh === 'function') {
        onRefresh();
      }
    } else {
      alert(data.message || 'Repost failed');
    }
  } catch (err) {
    console.error('Repost request failed:', err.message);
    alert('Network error: ' + err.message);
  } finally {
    setRepostSubmitting(false);
  }
};

const unrepostFromModal = async () => {
  const post = repostModalPost;
  if (!post || !user?.id) return;

  const targetId = getRepostTargetId(post);
  const key = String(targetId);

  setRepostSubmitting(true);
  try {
    const res = await fetch(`${API_URL}/repost.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        user_id: user.id,
        post_id: targetId,
        action: 'unrepost',
      }),
    });

    const data = await res.json();
    if (data.success) {
      setReposts((prev) => ({
        ...prev,
        [key]: typeof data.repost_count === 'number'
          ? data.repost_count
          : Math.max(0, (prev[key] || 1) - 1),
      }));
      setUserReposted((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setUserCaptions((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setRepostModalPost(null);
      if (typeof onRefresh === 'function') {
        onRefresh();
      }
    } else {
      alert(data.message || 'Failed to undo repost');
    }
  } catch (err) {
    console.error('Unrepost request failed:', err.message);
    alert('Network error: ' + err.message);
  } finally {
    setRepostSubmitting(false);
  }
};





function PostEditor() {
  const [editPost, setEditPost] = useState(null); // ✅ VALID
  const [showEdit, setShowEdit] = useState(false);

  return (
    <>
      {showEdit && editPost && (
        <Editpost
          post={editPost}
          onSuccess={() => {
            setShowEdit(false);
            setEditPost(null);
          }}
        />
      )}
    </>
  );
}

/// end of like buttons
const formatDate = (timestamp) => {
  const targetTimeZone = "Asia/Manila";

  // Convert timestamp to Manila time for accurate calculations
  const manilaTimeString = new Date(timestamp).toLocaleString("en-US", { timeZone: targetTimeZone });
  const date = new Date(manilaTimeString);
  
  const nowString = new Date().toLocaleString("en-US", { timeZone: targetTimeZone });
  const now = new Date(nowString);

  const diffSeconds = Math.floor((now - date) / 1000);

  if (diffSeconds < 60) return "just now";
  if (diffSeconds < 3600) {
    const minutes = Math.floor(diffSeconds / 60);
    return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
  }
  if (diffSeconds < 86400) {
    const hours = Math.floor(diffSeconds / 3600);
    return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  }

  // If more than a day old, show formatted Manila time
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: targetTimeZone
  }).format(date);
};

const handleNestedReplySubmit = async (postId, parentReplyId) => {
  const text = nestedReplyText[parentReplyId]?.trim();
  const files = nestedReplyMedia[parentReplyId] || [];

  if (!text && files.length === 0) return; // Prevent empty submission

  const formData = new FormData();
  formData.append("post_id", postId);
  formData.append("user_id", user.id);
  appendMentionedField(formData, "text", text || "");
  formData.append("parent_reply_id", parentReplyId);

  // Append files to FormData
  files.forEach((file, idx) => {
    formData.append("media[]", file);
  });

  try {
    const res = await fetch(`${API_URL}/add_reply.php`, {
      method: "POST",
      body: formData,
    });

    const data = await res.json();
    if (data.success) {
      // ✅ Update local post replies
       const resReplies = await fetch(`${API_URL}/get_replies.php?postId=${postId}&viewer_id=${user?.id || 0}`);
        const replyData = await resReplies.json();
  onRefresh();
      // Clear input and files
      setNestedReplyText((prev) => ({ ...prev, [parentReplyId]: "" }));
      setNestedReplyMedia((prev) => ({ ...prev, [parentReplyId]: [] }));
      setActiveReplyBox(null);
      setShowNestedReplies((prev) => ({ ...prev, [parentReplyId]: true }));
    }
  } catch (err) {
    console.error("Error posting nested reply:", err);
  }
};

const handleReplyChange = (postId, value) => {
  setReplyContents((prev) => ({ ...prev, [postId]: value }));
  setErrors((prev) => ({ ...prev, [postId]: '' }));
};


const handleReplySubmit = async (postId) => {
  const replyText = replyContents[postId]?.trim();
  const hasMedia = mediaFiles[postId] && mediaFiles[postId].length > 0;

  // Validate input
  if (!replyText && !hasMedia) {
    setErrors((prev) => ({ ...prev, [postId]: 'Please write a reply or attach media.' }));
    return;
  }

  setLoading((prev) => ({ ...prev, [postId]: true }));

  // Prepare FormData
  const formData = new FormData();
  formData.append('post_id', postId);
  formData.append('user_id', user.id);
  appendMentionedField(formData, 'text', replyText || '');

  if (hasMedia) {
    mediaFiles[postId].forEach((file) => formData.append('media[]', file));
  }

  try {
    // Submit reply
    const res = await fetch(`${API_URL}/add_reply.php`, { method: 'POST', body: formData });
    const rawText = await res.text();
    let data;

    try {
      data = JSON.parse(rawText);
    } catch (parseError) {
      console.error('Invalid JSON:', rawText);
      alert('Server returned invalid response. See console for details.');
      return;
    }

    if (data.success) {
      // Clear input and media
      setReplyContents((prev) => ({ ...prev, [postId]: '' }));
      setMediaFiles((prev) => ({ ...prev, [postId]: [] }));
      setShowReplyBox((prev) => ({ ...prev, [postId]: false }));
      setShowReplies((prev) => ({ ...prev, [postId]: true }));

      // Optionally refresh the entire post list
      onRefresh();

      // Fetch latest replies for this post
      try {
        const resReplies = await fetch(`${API_URL}/get_replies.php?postId=${postId}&viewer_id=${user?.id || 0}`);
        const replyData = await resReplies.json();

        if (replyData.success) {
          setReplies((prev) => ({ ...prev, [postId]: replyData.replies }));

          // Smooth scroll to the new reply
          setTimeout(() => {
            scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
          }, 100);
        } else {
          console.warn('Failed to refresh replies:', replyData.message);
        }
      } catch (refreshErr) {
        console.error('Error refreshing replies:', refreshErr);
      }
    } else {
      alert(data.message || 'Reply submission failed');
    }
  } catch (err) {
    console.error('Network error:', err);
    alert('Network or server error: ' + err.message);
  } finally {
    setLoading((prev) => ({ ...prev, [postId]: false }));
  }
};



const isImage = (url, type) => isMediaImage(url, type);
const isVideo = (url, type) => isMediaVideo(url, type);
const isAudio = (url) => /\.(mp3|wav|ogg)$/i.test(url);
const isDocument = (url) => /\.(pdf|docx?|pptx?|xlsx?)$/i.test(url);

const getMimeType = (url, type) => {
  if (isVideo(url, type) || isImage(url, type)) return getMediaMimeType(url, type);
  if (isAudio(url)) return 'audio/mpeg';
  if (isDocument(url)) return 'application/pdf';
  return 'application/octet-stream';
};



  const postsPerPage = 100;
const postList = isTypedFilter
  ? typedPosts
  : (Array.isArray(posts) ? posts : []);
const sortedPosts = preserveOrder || isTypedFilter
  ? postList
  : [...postList].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );

const currentPosts = hidePagination || isTypedFilter
  ? sortedPosts
  : sortedPosts.slice(
      (currentPage - 1) * postsPerPage,
      currentPage * postsPerPage
    );

const visiblePosts = currentPosts
  .filter((post) => {
    const q = searchQuery.trim().toLowerCase();
    const matchesSearch =
      q === '' ||
      post.title?.toLowerCase().includes(q) ||
      post.content?.toLowerCase().includes(q) ||
      post.username?.toLowerCase().includes(q) ||
      post.first_name?.toLowerCase().includes(q) ||
      post.last_name?.toLowerCase().includes(q) ||
      (post.post_type === 'repost' &&
        (post.original_post?.title?.toLowerCase().includes(q) ||
          post.original_post?.content?.toLowerCase().includes(q) ||
          post.original_post?.hiring_description?.toLowerCase().includes(q) ||
          post.original_post?.looking_for_job_description?.toLowerCase().includes(q) ||
          post.original_post?.username?.toLowerCase().includes(q) ||
          post.original_post?.first_name?.toLowerCase().includes(q) ||
          post.original_post?.last_name?.toLowerCase().includes(q)));

    const postCategory =
      post.post_type === 'repost' && post.original_post?.category
        ? post.original_post.category
        : post.category;
    const matchesCategory =
      selectedCategory === '' ||
      postCategory?.toLowerCase() === selectedCategory.toLowerCase() ||
      post.category?.toLowerCase() === selectedCategory.toLowerCase();

    const matchesType =
      typeFilter === 'all' || getEffectivePostType(post) === typeFilter;

    const fromBlocked =
      blockedAuthorIds.has(Number(post.user_id)) ||
      (post.original_post && blockedAuthorIds.has(Number(post.original_post.user_id)));

    return matchesSearch && matchesCategory && matchesType && !fromBlocked;
  })
  .sort((a, b) => {
    if (preserveOrder) return 0;
    if (sortType === 'latest') {
      return new Date(b.created_at) - new Date(a.created_at);
    }
    if (sortType === 'top') {
      return (b.likes_count || 0) - (a.likes_count || 0);
    }
    return 0;
  });

const totalPages = hidePagination || isTypedFilter
  ? 1
  : Math.ceil(sortedPosts.length / postsPerPage);

  
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };





useEffect(() => {
  const handleClickOutside = (event) => {
    if (!event.target.closest('.dropdown-trigger')) {
      setOpenDropdownId(null);
    }
  };
  document.addEventListener('mousedown', handleClickOutside);
  return () => document.removeEventListener('mousedown', handleClickOutside);
}, []);

 //replytoreply
 const submitNestedReply = async (e) => {
    e.preventDefault();
    if (!newReplyContent.trim()) {
      alert('Reply cannot be empty.');
      return;
    }
    if (!user) {
      alert('You must be logged in to reply.');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('post_id', posts.id); // The ID of the main post
      formData.append('user_id', user.id); // The ID of the logged-in user
      formData.append('content', newReplyContent.trim());
      formData.append('parent_reply_id', replyingToReplyId); // The ID of the reply being replied to

      // Make sure this URL points to your new backend script for creating nested replies
      const response = await fetch('your-backend-create-nested-reply-script.php', {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (result.success) {
        alert(result.message);
        setNewReplyContent(''); // Clear the input
        setReplyingToReplyId(null); // Close the reply form
        // Call a callback to inform the parent component to refresh or add the new reply
        if (onNewReplyCreated) {
          onNewReplyCreated(result.newReply); // Pass the newly created reply if returned by backend
        }
      } else {
        alert("Error: " + result.message); 
      }
    } catch (error) {
      console.error("Failed to submit nested reply:", error);
      alert("Failed to submit reply. Please try again.");
    }
  };
//delete reply

const handleDeleteReply = async (replyId) => {
  if (window.confirm("Are you sure you want to delete this reply?")) {
    try {
      const formData = new FormData();
      formData.append('reply_id', replyId);
      formData.append('user_id', user?.id || '');

      // Make sure this URL points to your specific reply deletion script (e.g., delete_reply.php)
      const response = await fetch(`${API_URL}/delete_reply.php`, {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (result.success) {
        alert(result.message);
            onRefresh();
        // This is where you update your component's state to remove the reply from the UI.
        // For example, if your replies are stored in a state variable called 'replies':
        // setReplies(prevReplies => prevReplies.filter(reply => reply.id !== replyId));
      } else {
        alert("Error: " + result.message);
      }
    } catch (error) {
      console.error("Failed to delete reply:", error);
      alert("Failed to delete reply. Please try again.");
    }
  }
};

// Renders one reply card plus its own replies, so a thread can go any depth.
const renderReplyCard = (post, reply, depth = 0) => {
  const replyText = String(reply.text || "");
  const replyWords = replyText.split(" ");
  const childReplies = getChildReplies(post.replies, reply.id);

  return (
    <article className={depth > 0 ? "rt-card rt-card--child" : "rt-card"}>
      <div className="rt-head">
        <img
          src={resolveAvatarUrl(reply, API_URL)}
          alt=""
          className="rt-avatar"
          onError={handleAvatarError}
        />
        <div className="rt-meta">
          <div className="rt-name">
            <AuthorNameLink user={reply} />
          </div>
          <div className="rt-time">{formatDate(reply.created_at)}</div>
        </div>
      </div>

      <p
        className="rt-body"
        dangerouslySetInnerHTML={{
          __html: createLinkifiedText(
            expandedContent[reply.id]
              ? replyText
              : replyWords.slice(0, 50).join(" ") +
                  (replyWords.length > 50 ? "..." : "")
          ),
        }}
      />

      {replyWords.length > 50 && (
        <button
          type="button"
          className="rt-see-more"
          onClick={() =>
            setExpandedContent((prev) => ({
              ...prev,
              [reply.id]: !prev[reply.id],
            }))
          }
        >
          {expandedContent[reply.id] ? "See less" : "See more"}
        </button>
      )}

      {Array.isArray(reply.media) && reply.media.length > 0 && (
        <div className="rt-media">
          {reply.media
            .slice(
              0,
              showAllImages[reply.id] ? reply.media.length : 2
            )
            .map((item, idx) => {
              const isImg = isImage(item.url);
              const isVid = isVideo(item.url);
              if (isImg) {
                return (
                  <img key={idx} src={item.url} alt="reply media" />
                );
              }
              if (isVid) {
                return (
                  <video key={idx} controls playsInline>
                    <source
                      src={item.url}
                      type={getMimeType(item.url)}
                    />
                  </video>
                );
              }
              return null;
            })}
          {reply.media.length > 2 && (
            <button
              type="button"
              className="rt-media-more"
              onClick={() =>
                setShowAllImages((prev) => ({
                  ...prev,
                  [reply.id]: !prev[reply.id],
                }))
              }
            >
              {showAllImages[reply.id]
                ? "Show less"
                : "See more media"}
            </button>
          )}
        </div>
      )}

      <div className="rt-actions">
        <ReactionButton
          compact
          pill
          iconSize={14}
          showLabel={false}
          userReaction={userReactions[`reply_${reply.id}`] || null}
          count={likes[`reply_${reply.id}`] || 0}
          onReact={(reactionType) =>
            handleLike(reply.id, "reply", reactionType)
          }
          onCountClick={() => setShowLikesModal(`reply_${reply.id}`)}
        />

        {user &&
          (Number(user.id) === Number(reply.user_id) ||
            user.role === "admin") && (
            <button
              type="button"
              className="rt-delete-btn"
              onClick={() => handleDeleteReply(reply.id)}
              aria-label="Delete reply"
            >
              <DeleteButtons />
            </button>
          )}

        {user && Number(user.id) !== Number(reply.user_id) && (
          <button
            type="button"
            className="rt-report-btn"
            onClick={() =>
              setReportTarget({ type: 'reply', id: reply.id, name: getAuthorDisplayName(reply) })
            }
            aria-label="Report comment"
            title="Report comment"
          >
            <Flag size={14} strokeWidth={2.2} />
          </button>
        )}

        <button
          type="button"
          className="rt-reply-btn"
          onClick={() =>
            setActiveReplyBox((prev) =>
              prev === reply.id ? null : reply.id
            )
          }
        >
          <Reply size={15} strokeWidth={2.25} />
          Reply
        </button>
      </div>

      {activeReplyBox === reply.id && (
        <div className="reply-composer" style={{ marginTop: 12 }}>
          <MentionInput
            userId={user?.id}
            showTools
            rows={4}
            placeholder="Write a reply... Type @ to mention a connection"
            value={nestedReplyText[reply.id] || ""}
            onChange={(value) =>
              setNestedReplyText((prev) => ({
                ...prev,
                [reply.id]: value,
              }))
            }
            style={{
              minHeight: "96px",
              padding: "12px 14px",
              marginBottom: 0,
            }}
          />
          <CaptionAssist
            kind="reply"
            text={nestedReplyText[reply.id] || ""}
            context={plainExcerpt(
              `Replying to: ${reply.text || ""}\nPost: ${post.content || ""}`,
              700
            )}
            userId={user?.id || user?.user_id}
            style={{ marginTop: 8 }}
            onApply={(next) =>
              setNestedReplyText((prev) => ({
                ...prev,
                [reply.id]: next,
              }))
            }
          />

          {(nestedReplyMedia[reply.id] || []).length > 0 && (
            <div className="reply-composer-previews">
              {(nestedReplyMedia[reply.id] || []).map((file, idx) => {
                const url = URL.createObjectURL(file);
                const isImageFile = file.type.startsWith("image/");
                const isVideoFile = file.type.startsWith("video/");

                return (
                  <div key={idx} className="reply-composer-preview">
                    <button
                      type="button"
                      className="reply-composer-preview-remove"
                      aria-label="Remove media"
                      onClick={(e) => {
                        e.stopPropagation();
                        setNestedReplyMedia((prev) => ({
                          ...prev,
                          [reply.id]: prev[reply.id].filter(
                            (_, i) => i !== idx
                          ),
                        }));
                      }}
                    >
                      ×
                    </button>
                    {isImageFile && <img src={url} alt="preview" />}
                    {isVideoFile && <video src={url} controls />}
                  </div>
                );
              })}
            </div>
          )}

          <div
            className="reply-composer-dropzone"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const files = Array.from(e.dataTransfer.files);
              setNestedReplyMedia((prev) => ({
                ...prev,
                [reply.id]: [...(prev[reply.id] || []), ...files],
              }));
            }}
            onClick={() =>
              document
                .getElementById(`nested-upload-${reply.id}`)
                .click()
            }
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                document
                  .getElementById(`nested-upload-${reply.id}`)
                  .click();
              }
            }}
          >
            <span
              className="reply-composer-dropzone-icon"
              aria-hidden="true"
            >
              <Upload size={18} strokeWidth={2.25} />
            </span>
            <p className="reply-composer-dropzone-text">
              Drag &amp; drop media here or{" "}
              <strong>click to browse</strong>
            </p>
            <input
              type="file"
              id={`nested-upload-${reply.id}`}
              accept="image/*,video/*"
              multiple
              style={{ display: "none" }}
              onChange={(e) => {
                const files = Array.from(e.target.files);
                setNestedReplyMedia((prev) => ({
                  ...prev,
                  [reply.id]: [...(prev[reply.id] || []), ...files],
                }));
              }}
            />
          </div>

          <button
            type="button"
            className="reply-composer-submit"
            onClick={() =>
              handleNestedReplySubmit(post.id, reply.id)
            }
          >
            <Send size={16} strokeWidth={2.25} />
            <span>Submit Reply</span>
          </button>
        </div>
      )}

      {childReplies.length > 0 && (
        <div
          className={
            depth + 1 >= MAX_REPLY_INDENT ? "rt-nested rt-nested--flat" : "rt-nested"
          }
        >
          <button
            type="button"
            className="rt-toggle"
            onClick={() =>
              setShowNestedReplies((prev) => ({
                ...prev,
                [reply.id]: !prev[reply.id],
              }))
            }
          >
            <span className="rt-toggle-icon" aria-hidden="true">
              {showNestedReplies[reply.id] ? (
                <ChevronUp size={14} strokeWidth={2.5} />
              ) : (
                <ChevronRight size={14} strokeWidth={2.5} />
              )}
            </span>
            <span>
              {showNestedReplies[reply.id]
                ? "Hide replies"
                : `View ${childReplies.length} repl${
                    childReplies.length > 1 ? "ies" : "y"
                  }`}
            </span>
          </button>

          {showNestedReplies[reply.id] &&
            childReplies.map((child) => (
              <div key={child.id} className="rt-nested-item">
                {renderReplyCard(post, child, depth + 1)}
              </div>
            ))}
        </div>
      )}
    </article>
  );
};

//
// 
const handleToggleSave = async (savedId, currentlySaved) => {
  if (!user?.id) return;
  const next = !currentlySaved;
  setSavedOverrides((prev) => ({ ...prev, [savedId]: next }));
  const result = await setPostSaved(user.id, savedId, next);
  if (!result.success) {
    setSavedOverrides((prev) => ({ ...prev, [savedId]: currentlySaved }));
    alert(result.message || 'Could not update saved posts.');
  }
};

const handleBlockAuthor = async (authorId, authorName) => {
  const blocked = await confirmAndBlock(user, { id: authorId, name: authorName });
  if (!blocked) return;
  setBlockedAuthorIds((prev) => new Set(prev).add(Number(authorId)));
  onRefresh();
};

// delete post" const PostList = ({ posts, onReply, user, onRefresh }) => {
  // Your other code...
const handleDelete = async (postId, postType) => {
  if (!postId || isNaN(postId)) {
    alert("❌ Invalid post ID .");
    return;
  }

  const confirmMsg =
    postType === "hiring"
      ? "Delete this job posting? All applications and resumes for this job will also be permanently deleted."
      : "Are you sure you want to delete this post?";
  if (!window.confirm(confirmMsg)) return;

  try {
    const formData = new FormData();
    formData.append('post_id', postId);
    if (user?.id) formData.append('user_id', user.id);

    const response = await fetch(`${API_URL }/delete_post.php`, {
      method: 'POST',
      body: formData,
    });  

    const text = await response.text();

    let result;
    try {
      result = JSON.parse(text);
    } catch (parseErr) {
      console.error("❌ Response is not valid JSON. Raw response:");
      console.error(text); // likely HTML error
      alert("❌ Server returned an invalid response. Check console for details.");
      return;
    }

    if (result.success) {
      alert("✅ Post deleted successfully.");
      if (typeof onRefresh === 'function') {
        onRefresh();
      }
    } else {
      alert("❌ Delete failed: " + (result.message || "Unknown error."));
    }
  } catch (err) {
    console.error("❌ Error while deleting post:", err);
    alert("❌ A network error occurred. See console for details.");
  }
};


const handleEdit = (post) => {
  setEditPost(post);
  setShowEditModal(true);
};

 
  const closeEditor = () => {
   setEditPost(false);
    setEditPost(null);
  };

return (

<div
  className="post-list"
  style={{
    width: '100%',
    maxWidth: '100%',
    margin: 0,
    padding: window.innerWidth <= 768 ? '0 0 80px' : '0 0 40px',
    boxSizing: 'border-box',
  }}
>
{/* Post Form */}

{!compact && (
<div className="feed-header">
  <h2 className="feed-title">
    Latest
    <span className="feed-title-accent">{' '}Discussion</span>
  </h2>

  <div className="feed-search-row">
    <span className="feed-search-icon" aria-hidden="true">
      <Search size={18} strokeWidth={2} />
    </span>
    <input
      type="text"
      className="feed-search-input"
      placeholder="Search discussion..."
      value={searchQuery}
      onChange={(e) => setSearchQuery(e.target.value)}
      aria-label="Search discussion"
    />

    <select
      className="feed-category-select"
      value={selectedCategory}
      onChange={(e) => setSelectedCategory(e.target.value)}
      aria-label="Filter by category"
    >
      <option value="">All Categories</option>
      {[...new Set(postList.map((p) => p.category))].map((category) => (
        <option key={category} value={category}>
          {category}
        </option>
      ))}
    </select>
  </div>
</div>
)}

{/*
      <select
        value={sortType}
        onChange={(e) => setSortType(e.target.value)}
     style={{
      flex: 1,
      padding: '10px',
      borderRadius: '8px',
      border: '1px solid #ccc',
      fontSize: '12px',
      width: '100%',
      height: '40px', // same height as input
      boxSizing: 'border-box',
      display: window.innerWidth <= 768 ? "none" : "block"
    }}
      >
        <option value="latest">Latest</option>
        <option value="top">Top</option>
      </select>*/}
 
   

 {!compact && <PostForm onSubmit={onRefresh} user={user} />}

 {!compact && (
 <div className="feed-filter" role="tablist" aria-label="Filter discussions">
   {TYPE_TABS.map((tab) => {
     const hiringBadge =
       tab.id === 'hiring' &&
       isEmployerUser &&
       hiringInboxCount > 0 &&
       !hiringBadgeDismissed
         ? hiringInboxCount
         : 0;
     const openToWorkBadge =
       tab.id === 'looking_for_job' &&
       isSeekerUser &&
       profileViewCount > 0 &&
       !profileBadgeDismissed
         ? profileViewCount
         : 0;
     const tabBadge = hiringBadge || openToWorkBadge;
     return (
     <button
       key={tab.id}
       type="button"
       role="tab"
       aria-selected={typeFilter === tab.id}
       className={`feed-filter-tab${typeFilter === tab.id ? ' is-active' : ''}`}
       onClick={() => {
         setTypeFilter(tab.id);
         setCurrentPage(1);
         if (tab.id === 'hiring' && isEmployerUser) {
           setHiringBadgeDismissed(true);
         }
         if (tab.id === 'looking_for_job' && isSeekerUser) {
           setProfileBadgeDismissed(true);
         }
       }}
     >
       <span>{tab.label}</span>
       {tabBadge > 0 && (
         <span className="feed-filter-badge">
           {tabBadge > 99 ? '99+' : tabBadge}
         </span>
       )}
     </button>
     );
   })}
   {(showEmployerInbox || showSeekerInbox) && (
     <Link
       to={inboxTo}
       className="feed-filter-tab feed-filter-inbox"
       title={inboxTitle}
     >
       <span>Inbox</span>
       {inboxCount > 0 && (
         <span
           className="feed-filter-badge"
           aria-label={inboxTitle}
         >
           {inboxCount > 99 ? '99+' : inboxCount}
         </span>
       )}
     </Link>
   )}
 </div>
 )}

 <ReportModal
   open={Boolean(reportTarget)}
   target={reportTarget}
   user={user}
   onClose={() => setReportTarget(null)}
 />

 {/* Edit Post Modal */}
 {showEditModal && editPost?.post_type === 'hiring' && (
   <HiringForm
     post={editPost}
     user={user}
     onClose={() => {
       setShowEditModal(false);
       setEditPost(null);
     }}
     onSuccess={() => {
       setShowEditModal(false);
       setEditPost(null);
       if (typeof onRefresh === 'function') onRefresh();
     }}
     onRefresh={onRefresh}
   />
 )}
 {showEditModal && editPost?.post_type === 'looking_for_job' && (
   <LookingForJobForm
     post={editPost}
     user={user}
     onClose={() => {
       setShowEditModal(false);
       setEditPost(null);
     }}
     onSuccess={() => {
       setShowEditModal(false);
       setEditPost(null);
       if (typeof onRefresh === 'function') onRefresh();
     }}
     onRefresh={onRefresh}
   />
 )}
 {showEditModal && editPost && !['hiring', 'looking_for_job'].includes(editPost.post_type) && (
   <div
     style={{
       position: 'fixed',
       top: 0,
       left: 0,
       width: '100%',
       height: '100%',
       backgroundColor: 'rgba(0, 0, 0, 0.6)',
       display: 'flex',
       alignItems: 'center',
       justifyContent: 'center',
       zIndex: 2000,
     }}
     onClick={() => {
       setShowEditModal(false);
       setEditPost(null);
     }}
   >
     <div
       onClick={(e) => e.stopPropagation()}
       style={{
         backgroundColor: '#fff',
         borderRadius: '12px',
         padding: '20px',
         maxWidth: '600px',
         width: '90%',
         maxHeight: '90vh',
         overflowY: 'auto',
       }}
     >
       {editPost?.post_type === 'article' ? (
         <EditArticleForm
           post={editPost}
           user={user}
           onSuccess={() => {
             setShowEditModal(false);
             setEditPost(null);
             if (typeof onRefresh === 'function') {
               onRefresh();
             }
           }}
         />
       ) : editPost?.post_type === 'blog' ? (
         <EditBlogForm
           post={editPost}
           user={user}
           onSuccess={() => {
             setShowEditModal(false);
             setEditPost(null);
             if (typeof onRefresh === 'function') {
               onRefresh();
             }
           }}
         />
       ) : (
         <Editpost
           post={editPost}
           user={user}
           onSuccess={() => {
             setShowEditModal(false);
             setEditPost(null);
             if (typeof onRefresh === 'function') {
               onRefresh();
             }
           }}
         />
       )}
     </div>
   </div>
 )}

    {/* Survey tab — surveys live in the discussion forum */}
    {typeFilter === 'survey' ? (
      <div className="feed-survey-panel">
        <SurveyPanel
          user={user}
          page="main"
          adminMode={isAdmin(user)}
        />
      </div>
    ) : typedLoading && visiblePosts.length === 0 ? (
      <p className="feed-filter-empty">Loading…</p>
    ) : visiblePosts.length === 0 ? (
      <p className="feed-filter-empty">
        {typeFilter === 'all'
          ? 'No discussions found.'
          : `No ${TYPE_TABS.find((t) => t.id === typeFilter)?.label?.toLowerCase() || 'posts'} yet.`}
      </p>
    ) : (
    <>
    {visiblePosts.map((post) => (
      <div   key={post.id}
        className="post-card"
        style={{
   borderRadius: window.innerWidth <= 768 ? '0' : '16px',
  padding: window.innerWidth <= 768 ? '20px 5px' : '24px 28px', // remove side padding on mobile
  marginBottom: '16px',
  backgroundColor: '#ffffff',
  boxShadow:
    window.innerWidth <= 768
      ? 'none'
      : '0 8px 24px rgba(0, 0, 0, 0.06), 0 4px 8px rgba(0, 0, 0, 0.03)',
  borderTop: '1px solid #eaeaea',
  borderBottom: '1px solid #eaeaea',
  borderLeft: window.innerWidth <= 768 ? 'none' : '1px solid #eaeaea',
  borderRight: window.innerWidth <= 768 ? 'none' : '1px solid #eaeaea',
  transition: 'all 0.3s ease',
     flexDirection: window.innerWidth <= 768 ? "column" : "row",

  width: '100%',
  maxWidth: '100%',
  marginLeft: 0,
  marginRight: 0,

  boxSizing: 'border-box',
  overflow: 'visible',
  overflowWrap: 'anywhere',

}}

      >
      <div style={{ position: 'relative' }}>


{user && (
  <div className="am-anchor">
    <div className="am-wrap dropdown-trigger">
      <button
        type="button"
        className="am-trigger"
        aria-label="Post options"
        aria-expanded={Boolean(showDropdown?.[post.id])}
        onClick={() => setShowDropdown((prev) => ({ ...prev, [post.id]: !prev[post.id] }))}
      >
        ⋮
      </button>

      {showDropdown?.[post.id] && (
        <div className="am-menu" role="menu">
          {(() => {
            const savedId = getRepostTargetId(post);
            const isSaved = savedOverrides[String(savedId)] ?? Boolean(post.is_saved);
            return (
              <button
                type="button"
                role="menuitem"
                className="am-item"
                aria-pressed={isSaved}
                onClick={() => {
                  handleToggleSave(savedId, isSaved);
                  setShowDropdown((prev) => ({ ...prev, [post.id]: false }));
                }}
              >
                <span className="am-icon am-icon--save" aria-hidden="true">
                  <Bookmark strokeWidth={2.2} fill={isSaved ? 'currentColor' : 'none'} />
                </span>
                <span className="am-label">{isSaved ? 'Remove from saved' : 'Save post'}</span>
              </button>
            );
          })()}

          {canManagePost(user, post) ? (
          <>
          <button
            type="button"
            role="menuitem"
            className="am-item"
            onClick={() => {
              handleEdit(post);
              setShowDropdown((prev) => ({ ...prev, [post.id]: false }));
            }}
          >
            <span className="am-icon am-icon--edit" aria-hidden="true">
              <Pencil strokeWidth={2.2} />
            </span>
            <span className="am-label">Edit</span>
          </button>

          <button
            type="button"
            role="menuitem"
            className="am-item am-item--danger"
            onClick={() => {
              handleDelete(post.id, post.post_type);
              setShowDropdown((prev) => ({ ...prev, [post.id]: false }));
            }}
          >
            <span className="am-icon am-icon--delete" aria-hidden="true">
              <Trash2 strokeWidth={2.2} />
            </span>
            <span className="am-label">Delete</span>
          </button>
          </>
          ) : (
          <>
          <button
            type="button"
            role="menuitem"
            className="am-item"
            onClick={() => {
              setReportTarget({ type: 'post', id: post.id, name: getAuthorDisplayName(post) });
              setShowDropdown((prev) => ({ ...prev, [post.id]: false }));
            }}
          >
            <span className="am-icon am-icon--report" aria-hidden="true">
              <Flag strokeWidth={2.2} />
            </span>
            <span className="am-label">Report post</span>
          </button>

          {!post.from_admin && (
            <button
              type="button"
              role="menuitem"
              className="am-item am-item--danger"
              onClick={() => {
                setShowDropdown((prev) => ({ ...prev, [post.id]: false }));
                handleBlockAuthor(post.user_id, getAuthorDisplayName(post));
              }}
            >
              <span className="am-icon am-icon--block" aria-hidden="true">
                <Ban strokeWidth={2.2} />
              </span>
              <span className="am-label">Block {post.first_name || 'author'}</span>
            </button>
          )}
          </>
          )}
        </div>
      )}
    </div>
  </div>
)}

      
      {/* 🔹Title */}
     

      {/* 🔹User Info */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
        <img
          src={resolveAvatarUrl(post, API_URL)}
          alt="avatar"
          style={{
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            objectFit: 'cover',
            marginRight: '12px'
          }}
          onError={handleAvatarError}
        />
        <div>
          <div style={postAuthorStyle}>
            <AuthorNameLink user={post} style={{ fontWeight: 600 }} />
            {post.post_type === 'repost' && (
              <span style={{ fontWeight: 500, color: '#65676b', marginLeft: 6 }}>
                reposted
              </span>
            )}
          </div>
          <div style={postMetaStyle}>
            {formatAuthorMetaLine(post, formatDate)}
          </div>
        </div>
      </div>
 
 {post.post_type === 'article' ? (
   <ArticleCard post={post} linkPrefix={user ? '/home/article' : '/home/public'} />
 ) : post.post_type === 'blog' ? (
   <ArticleCard post={post} linkPrefix={user ? '/home/blog' : '/home/public'} {...BLOG_CARD_PROPS} />
 ) : post.post_type === 'hiring' ? (
   <HiringCard post={post} linkPrefix={user ? '/home/hiring' : '/home/public'} />
 ) : post.post_type === 'looking_for_job' ? (
   <LookingForJobCard post={post} linkPrefix={user ? '/home/looking-for-job' : '/home/public'} />
 ) : post.post_type === 'question' ? (
   <>
     <QuestionCard post={post} linkPrefix={user ? '/home/question' : '/home/public'} />
     {Array.isArray(post.media) && post.media.length > 0 && (
       <div style={{ marginTop: 10 }}>
         <PostMediaGrid media={post.media} onImageClick={setPopupPhoto} />
       </div>
     )}
   </>
 ) : post.post_type === 'repost' ? (
   <>
     {!!String(post.content || post.repost_caption || '').trim() &&
      String(post.content || post.repost_caption || '').trim() !== 'Repost' && (
       <p
         style={{ ...postContentStyle, marginBottom: '12px' }}
         dangerouslySetInnerHTML={{
           __html: createLinkifiedText(String(post.content || post.repost_caption || '')),
         }}
       />
     )}
     {post.original_post && (() => {
       const original = post.original_post;
       const originalType = original.post_type || 'text';
       const discussionPath = !user
         ? `/home/public/${original.id}`
         : originalType === 'hiring'
           ? `/home/hiring/${original.id}`
           : originalType === 'looking_for_job'
             ? `/home/looking-for-job/${original.id}`
             : originalType === 'article'
               ? `/home/article/${original.id}`
               : originalType === 'blog'
                 ? `/home/blog/${original.id}`
                 : originalType === 'question'
                   ? `/home/question/${original.id}`
                   : `/home/post/${original.id}`;
       const excerptSource =
         original.hiring_description ||
         original.looking_for_job_description ||
         original.text ||
         original.content ||
         '';
       const excerpt = htmlToExcerpt(excerptSource, 160);

       return (
         <div
           style={{
             border: '1px solid #e5e7eb',
             borderRadius: 12,
             padding: 14,
             background: '#fafafa',
             marginBottom: 8,
             overflow: 'hidden',
             maxWidth: '100%',
             boxSizing: 'border-box',
           }}
         >
           <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
             <img
               src={resolveAvatarUrl(original, API_URL)}
               alt=""
               onError={handleAvatarError}
               style={{
                 width: 36,
                 height: 36,
                 borderRadius: '50%',
                 objectFit: 'cover',
                 background: '#e5e7eb',
                 flexShrink: 0,
               }}
             />
             <div style={{ minWidth: 0, flex: 1 }}>
               <div
                 style={{
                   fontWeight: 600,
                   fontSize: 14,
                   color: '#111827',
                   overflowWrap: 'anywhere',
                 }}
               >
                 {getAuthorDisplayName(original)}
               </div>
               <div
                 style={{
                   fontSize: 12,
                   color: '#6b7280',
                   overflowWrap: 'anywhere',
                 }}
               >
                 {formatAuthorMetaLine(original, formatDate)}
               </div>
             </div>
           </div>

           {originalType === 'hiring' && (
             <div
               style={{
                 display: 'inline-block',
                 fontSize: 11,
                 fontWeight: 600,
                 letterSpacing: 0.4,
                 textTransform: 'uppercase',
                 color: '#0f766e',
                 background: '#ccfbf1',
                 padding: '3px 8px',
                 borderRadius: 4,
                 marginBottom: 8,
               }}
             >
               Hiring
             </div>
           )}

           {originalType === 'looking_for_job' && (
             <div
               style={{
                 display: 'inline-block',
                 fontSize: 11,
                 fontWeight: 600,
                 letterSpacing: 0.4,
                 textTransform: 'uppercase',
                 color: '#1d4ed8',
                 background: '#dbeafe',
                 padding: '3px 8px',
                 borderRadius: 4,
                 marginBottom: 8,
               }}
             >
               Looking for job
             </div>
           )}

           {original.title && original.title !== 'Repost' && (
             <div
               style={{
                 fontWeight: 700,
                 fontSize: 16,
                 color: '#102a43',
                 marginBottom: 6,
                 overflowWrap: 'anywhere',
                 wordBreak: 'break-word',
               }}
             >
               {original.title}
             </div>
           )}

           {(original.hiring_company || original.hiring_location) && (
             <div
               style={{
                 fontSize: 13,
                 color: '#475569',
                 marginBottom: 6,
                 overflowWrap: 'anywhere',
               }}
             >
               {[original.hiring_company, original.hiring_location]
                 .filter(Boolean)
                 .join(' · ')}
             </div>
           )}

           {(original.looking_for_job_location || original.looking_for_job_availability) && (
             <div
               style={{
                 fontSize: 13,
                 color: '#475569',
                 marginBottom: 6,
                 overflowWrap: 'anywhere',
               }}
             >
               {[original.looking_for_job_location, original.looking_for_job_availability]
                 .filter(Boolean)
                 .join(' · ')}
             </div>
           )}

           {excerpt && (
             <div
               style={{
                 fontSize: 14,
                 color: '#374151',
                 lineHeight: 1.5,
                 overflowWrap: 'anywhere',
                 wordBreak: 'break-word',
                 maxWidth: '100%',
               }}
             >
               {excerpt}
             </div>
           )}

           {Array.isArray(original.media) && original.media[0] && (
             <div
               style={{
                 marginTop: 10,
                 borderRadius: 10,
                 overflow: 'hidden',
                 maxWidth: '100%',
               }}
             >
               {isImage(original.media[0].url, original.media[0].type) ? (
                 <img
                   src={original.media[0].url}
                   alt=""
                   style={{
                     width: '100%',
                     maxHeight: 280,
                     objectFit: 'cover',
                     display: 'block',
                   }}
                 />
               ) : isVideo(original.media[0].url, original.media[0].type) ? (
                 <video
                   controls
                   playsInline
                   preload="metadata"
                   style={{
                     width: '100%',
                     maxHeight: 280,
                     background: '#000',
                     display: 'block',
                   }}
                 >
                   <source
                     src={original.media[0].url}
                     type={getMimeType(original.media[0].url, original.media[0].type)}
                   />
                 </video>
               ) : null}
             </div>
           )}

           <Link
             to={discussionPath}
             style={{
               display: 'inline-block',
               marginTop: 12,
               fontSize: 13,
               fontWeight: 600,
               color: '#0a66c2',
               textDecoration: 'none',
             }}
           >
             View discussion →
           </Link>
         </div>
       );
     })()}
   </>
 ) : (
   <>
 <h3 style={postTitleStyle}>
   <Link
     to={user ? `/home/post/${post.id}` : `/home/public/${post.id}`}
     style={postTitleLinkStyle}
   >
              {post.title}
            </Link>
      </h3>
      {/* 🔹Content */}
<p
  style={postContentStyle}
  dangerouslySetInnerHTML={{
    __html:
      expandedContent[post.id] || String(post.content || '').split(" ").length <= 50
        ? createLinkifiedText(String(post.content || ''))
        : createLinkifiedText(
            String(post.content || '').split(" ").slice(0, 50).join(" ") + "..."
          ),
  }}
></p>

{String(post.content || '').split(" ").length > 50 && (
  <button
    onClick={() =>
      setExpandedContent((prev) => ({
        ...prev,
        [post.id]: expandedContent[post.id] ? null : post.content,
      }))
    }
    style={{
      background: "none",
      border: "none",
      color: "#2563eb",
      cursor: "pointer",
      fontSize: postFonts.link,
      padding: 0,
    }}
  >
    {expandedContent[post.id] ? "See less" : "See more"}
  </button>
)}
      <div>
      {Array.isArray(post.media) && post.media.length > 0 && (
        <PostMediaGrid
          media={post.media}
          onImageClick={setPopupPhoto}
        />
      )}

    </div>

      </>
      )}

      </div>
    



    
      {/* Facebook-style Reactions and Actions Section */}
      {(() => {
        const postKey = `post_${post.id}`;
        const likeCount = likes[postKey] || 0;
        const likedUsers = whoLiked[postKey] || [];
        const mainReplies = post.replies?.filter(
          (reply) => !reply.parent_reply_id || reply.parent_reply_id === 0
        ) || [];
        const replyCount = mainReplies.length;
        const repostTargetId = getRepostTargetId(post);
        const repostKey = String(repostTargetId);
        const repostCount = reposts[repostKey] || 0;
        const hasUserReposted = !!userReposted[repostKey];

        // Format names for display (e.g., "Cathrine Biñas Canlas, Ann Jacobe and 13 others")
        const formatReactionNames = () => {
          if (likedUsers.length === 0) return null;
          if (likedUsers.length === 1) return getLikerDisplayName(likedUsers[0]);
          if (likedUsers.length === 2) {
            return `${getLikerDisplayName(likedUsers[0])} and ${getLikerDisplayName(likedUsers[1])}`;
          }
          const names = likedUsers.slice(0, 2).map((u) => getLikerDisplayName(u)).join(', ');
          return `${names} and ${likedUsers.length - 2} others`;
        };

        const reactionText = formatReactionNames();
        const topReactions = getTopReactions(likedUsers, 3);

        return (
          <div style={{ marginTop: '12px' }}>
            {/* Reactions Display (Top) - Shows reaction icons and names */}
            {(likeCount > 0 || repostCount > 0) && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '8px',
                  marginBottom: '8px',
                  padding: '0 4px',
                }}
              >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: likeCount > 0 ? 'pointer' : 'default',
                  flex: 1,
                  minWidth: 0,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (likeCount > 0) setShowLikesModal(postKey);
                }}
                onMouseEnter={(e) => {
                  if (likeCount > 0) e.currentTarget.style.textDecoration = 'underline';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.textDecoration = 'none';
                }}
              >
                {likeCount > 0 && (
                  <>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {topReactions.map((type, idx) => (
                    <span
                      key={type}
                      style={{
                        marginLeft: idx === 0 ? 0 : -4,
                        zIndex: topReactions.length - idx,
                        background: '#fff',
                        borderRadius: '50%',
                        width: 18,
                        height: 18,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 0 0 1px #fff',
                      }}
                    >
                      <ReactionIcon type={type} size={12} />
                    </span>
                  ))}
                </div>
                {/* Names Text */}
                {reactionText && (
                  <span
                    style={{
                      fontSize: postFonts.reaction,
                      color: '#65676b',
                    }}
                  >
                    {reactionText}
                  </span>
                )}
                  </>
                )}
              </div>
              {repostCount > 0 && (
                <span style={{ fontSize: postFonts.reaction, color: '#65676b', whiteSpace: 'nowrap' }}>
                  {repostCount} repost{repostCount === 1 ? '' : 's'}
                </span>
              )}
              </div>
            )}

            {/* Separator Line */}
            <div
              style={{
                height: '1px',
                backgroundColor: '#e4e6eb',
                margin: '8px 0',
              }}
            />

            {/* Action Buttons (Bottom) */}
            <div
              className="post-action-bar"
              style={{
                display: 'flex',
                justifyContent: 'space-around',
                alignItems: 'center',
                padding: '4px 0',
              }}
            >
              <ReactionButton
                userReaction={userReactions[postKey] || null}
                onReact={(reactionType) => handleLike(post.id, 'post', reactionType)}
                iconSize={16}
              />

              {/* Reply Button */}
              <button
                type="button"
                onClick={() =>
                  setShowReplyBox((prev) => ({ ...prev, [post.id]: !prev[post.id] }))
                }
                aria-label="Reply"
                title="Reply"
                style={{
                  background: 'transparent',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0px',
                  padding: '6px 12px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  flex: 1,
                  justifyContent: 'center',
                  color: '#65676b',
                  fontWeight: '600',
                  fontSize: postFonts.action,
                  transition: 'background-color 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f2f2f2';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <ReplyButton />
              </button>

              {/* Repost Button */}
              <button
                onClick={() => openRepostModal(post)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0px',
                  padding: '6px 12px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  flex: 1,
                  justifyContent: 'center',
                  color: hasUserReposted ? '#0a66c2' : '#65676b',
                  fontWeight: '600',
                  fontSize: postFonts.action,
                  transition: 'background-color 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f2f2f2';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
                aria-label={hasUserReposted ? 'Edit repost' : 'Repost'}
                title={hasUserReposted ? 'Edit repost' : 'Repost'}
              >
                <RepostButton
                  count={repostCount}
                  active={hasUserReposted}
                />
              </button>

              {/* Share Button */}
              <button
                onClick={() => {
                  const shareId = repostTargetId;
                  const shareUrl = `${window.location.origin}/home/public/${shareId}`;
                  recordInteraction('post_share', `/home/public/${shareId}`, user?.id || 0);

                  if (navigator.share) {
                    navigator
                      .share({
                        title: "ReallyLesson Post",
                        url: shareUrl,
                      })
                      .catch((err) => {
                        console.warn("Native share failed:", err);

                        // Fallback: open in new tab
                        const link = document.createElement("a");
                        link.href = shareUrl;
                        link.target = "_blank";
                        link.rel = "noopener noreferrer";
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      });
                  } else {
                    // Same fallback if native share isn't supported
                    const link = document.createElement("a");
                    link.href = shareUrl;
                    link.target = "_blank";
                    link.rel = "noopener noreferrer";
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0px',
                  padding: '6px 12px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  flex: 1,
                  justifyContent: 'center',
                  color: '#65676b',
                  fontWeight: '600',
                  fontSize: postFonts.action,
                  transition: 'background-color 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f2f2f2';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
                aria-label="Share"
                title="Share"
              >
                <ShareButton />
              </button>
            </div>

            {/* View Replies Link (if there are replies) */}
            {replyCount > 0 && !showReplyBox[post.id] && (
              <div style={{ padding: '4px 12px', marginTop: '4px' }}>
                <button
                  type="button"
                  className="reply-view-link"
                  onClick={() =>
                    setShowReplies((prev) => ({
                      ...prev,
                      [post.id]: !prev[post.id],
                    }))
                  }
                >
                  <span className="rt-toggle-icon" aria-hidden="true">
                    {showReplies[post.id] ? (
                      <ChevronUp size={14} strokeWidth={2.5} />
                    ) : (
                      <ChevronRight size={14} strokeWidth={2.5} />
                    )}
                  </span>
                  <span>
                    {showReplies[post.id]
                      ? 'Hide replies'
                      : `View ${replyCount} repl${replyCount === 1 ? 'y' : 'ies'}`}
                  </span>
                </button>
              </div>
            )}
          </div>
        );
      })()}

 {/* Like & Share Buttons - end */}




       {showReplyBox[post.id] && (() => {
  const composerReplyCount = (post.replies || []).filter(
    (reply) => !reply.parent_reply_id || reply.parent_reply_id === 0
  ).length;
  return (
  <div className="reply-composer">
    {composerReplyCount > 0 && (
      <button
        type="button"
        className="reply-composer-view"
        onClick={() =>
          setShowReplies((prev) => ({
            ...prev,
            [post.id]: !prev[post.id],
          }))
        }
      >
        <span className="rt-toggle-icon" aria-hidden="true">
          {showReplies[post.id] ? (
            <ChevronUp size={14} strokeWidth={2.5} />
          ) : (
            <ChevronRight size={14} strokeWidth={2.5} />
          )}
        </span>
        <span>
          {showReplies[post.id]
            ? 'Hide replies'
            : `View ${composerReplyCount} repl${composerReplyCount === 1 ? 'y' : 'ies'}`}
        </span>
      </button>
    )}

    <MentionInput
      userId={user?.id}
      showTools
      placeholder="Write a reply... Type @ to mention a connection"
      value={replyContents[post.id] || ''}
      onChange={(value) => handleReplyChange(post.id, value)}
      rows={4}
      style={{
        minHeight: '96px',
        padding: '12px 14px',
        marginBottom: 0,
      }}
    />
    <CaptionAssist
      kind="reply"
      text={replyContents[post.id] || ''}
      context={plainExcerpt(post.content || post.title || '', 700)}
      userId={user?.id || user?.user_id}
      style={{ marginTop: 8 }}
      onApply={(next) => handleReplyChange(post.id, next)}
    />

    {errors[post.id] && (
      <p className="reply-composer-error">{errors[post.id]}</p>
    )}

    {mediaFiles[post.id]?.length > 0 && (
      <div className="reply-composer-previews">
        {mediaFiles[post.id].map((file, idx) => {
          const url = URL.createObjectURL(file);
          const isImage = file.type.startsWith('image/');
          const isVideo = file.type.startsWith('video/');

          return (
            <div key={idx} className="reply-composer-preview">
              <button
                type="button"
                className="reply-composer-preview-remove"
                aria-label="Remove media"
                onClick={() => {
                  const updated = [...mediaFiles[post.id]];
                  updated.splice(idx, 1);
                  setMediaFiles((prev) => ({ ...prev, [post.id]: updated }));
                }}
              >
                ×
              </button>
              {isImage && <img src={url} alt="preview" />}
              {isVideo && <video src={url} controls />}
            </div>
          );
        })}
      </div>
    )}

    <div
      className={`reply-composer-dropzone${isDragging[post.id] ? ' is-dragging' : ''}`}
      onDrop={(e) => {
        e.preventDefault();
        const files = Array.from(e.dataTransfer.files);
        setMediaFiles((prev) => ({
          ...prev,
          [post.id]: prev[post.id] ? [...prev[post.id], ...files] : files,
        }));
        setIsDragging((prev) => ({ ...prev, [post.id]: false }));
      }}
      onDragOver={(e) => {
        e.preventDefault();
        setIsDragging((prev) => ({ ...prev, [post.id]: true }));
      }}
      onDragLeave={() => {
        setIsDragging((prev) => ({ ...prev, [post.id]: false }));
      }}
      onClick={() => document.getElementById(`mediaInput-${post.id}`).click()}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          document.getElementById(`mediaInput-${post.id}`).click();
        }
      }}
    >
      <span className="reply-composer-dropzone-icon" aria-hidden="true">
        <Upload size={18} strokeWidth={2.25} />
      </span>
      <p className="reply-composer-dropzone-text">
        Drag &amp; drop media here or <strong>click to browse</strong>
      </p>
    </div>

    <input
      id={`mediaInput-${post.id}`}
      type="file"
      accept="image/*,video/*"
      multiple
      onChange={(e) => {
        const files = Array.from(e.target.files);
        setMediaFiles((prev) => ({
          ...prev,
          [post.id]: prev[post.id] ? [...prev[post.id], ...files] : files,
        }));
      }}
      style={{ display: 'none' }}
    />

    <button
      type="button"
      className="reply-composer-submit"
      onClick={() => handleReplySubmit(post.id)}
      disabled={loading[post.id]}
    >
      <Send size={16} strokeWidth={2.25} />
      <span>{loading[post.id] ? 'Submitting...' : 'Submit Reply'}</span>
    </button>
  </div>
  );
})()}




{showReplies[post.id] && (
  <div className="rt-list">
    <AnimatePresence>
      {(post.replies || [])
        .filter((r) => !r.parent_reply_id)
        .slice(0, visibleReplies[post.id] ?? 7)
        .map((reply, i) => (
          <motion.div
            key={reply.id || i}
            ref={
              i === ((visibleReplies[post.id] ?? 7) - 1)
                ? scrollRef
                : null
            }
            initial={{ opacity: 0, translateY: 10 }}
            animate={{ opacity: 1, translateY: 0 }}
            exit={{ opacity: 0, translateY: -10 }}
            transition={{ duration: 0.3 }}
          >
            {renderReplyCard(post, reply)}
          </motion.div>
        ))}
    </AnimatePresence>
  </div>
)}


      </div>
    ))}
    {isTypedFilter && typedHasMore && (
      <button
        type="button"
        className="feed-filter-load-more"
        disabled={typedLoading}
        onClick={() => loadTypedPosts({ append: true, offset: typedOffset })}
      >
        {typedLoading ? 'Loading…' : 'Load more'}
      </button>
    )}
    </>
    )}

    

{/* Pagination controls*/}
     {typeFilter !== 'survey' && !hidePagination && totalPages > 1 && (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      marginTop: '20px',
      gap: '10px',
      flexWrap: 'wrap'
    }}
  >
    <button
      disabled={currentPage === 1}
      onClick={() => handlePageChange(currentPage - 1)}
      style={{
        background: 'transparent',
        border: '2px solid #4a90e2',
        color: '#4a90e2',
        padding: '8px 16px',
        borderRadius: '6px',
        fontSize: '14px',
        cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
        opacity: currentPage === 1 ? 0.5 : 1,
        transition: 'all 0.3s ease'
      }}
      onMouseEnter={(e) => {
        if (currentPage !== 1) {
          e.target.style.background = '#4a90e2';
          e.target.style.color = '#fff';
        }
      }}
      onMouseLeave={(e) => {
        if (currentPage !== 1) {
          e.target.style.background = 'transparent';
          e.target.style.color = '#4a90e2';
        }
      }}
    >
      &laquo; Prev
    </button>

    <span style={{ alignSelf: 'center', fontSize: '14px', color: '#555' }}>
      Page {currentPage} of {totalPages}
    </span>

    <button
      disabled={currentPage === totalPages}
      onClick={() => handlePageChange(currentPage + 1)}
      style={{
        background: 'transparent',
        border: '2px solid #4a90e2',
        color: '#4a90e2',
        padding: '8px 16px',
        borderRadius: '6px',
        fontSize: '14px',
        cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
        opacity: currentPage === totalPages ? 0.5 : 1,
        transition: 'all 0.3s ease'
      }}
      onMouseEnter={(e) => {
        if (currentPage !== totalPages) {
          e.target.style.background = '#4a90e2';
          e.target.style.color = '#fff';
        }
      }}
      onMouseLeave={(e) => {
        if (currentPage !== totalPages) {
          e.target.style.background = 'transparent';
          e.target.style.color = '#4a90e2';
        }
      }}
    >
      Next &raquo;
    </button>
  </div>
)}

<PhotoLightbox src={popupPhoto} onClose={closePopup} />

{/* 🔹 Who Liked Panel (click-to-open, no profile pictures, Facebook-style) */}
{showLikesModal && whoLiked[showLikesModal] && whoLiked[showLikesModal].length > 0 && (() => {
  const list = whoLiked[showLikesModal];
  const visibleCount = 20;
  const hasMore = list.length > visibleCount;
  const visible = hasMore ? list.slice(0, visibleCount) : list;
  const remaining = list.length - visible.length;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1200,
        backgroundColor: 'transparent',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-end',
        paddingBottom: 80,
      }}
      onClick={() => setShowLikesModal(null)}
    >
      <div
        style={{
          backgroundColor: 'rgba(74, 76, 83, 0.98)',
          color: '#f9fafb',
          borderRadius: '10px',
          padding: '10px 14px',
          maxWidth: '320px',
          maxHeight: '260px',
          overflowY: 'auto',
          boxShadow: '0 18px 35px rgba(155, 162, 180, 0.55)',
          fontSize: '12px',
          position: 'relative',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={() => setShowLikesModal(null)}
          style={{
            position: 'absolute',
            top: 4,
            right: 6,
            background: 'transparent',
            border: 'none',
            color: '#e5e7eb',
            cursor: 'pointer',
            fontSize: 14,
          }}
          aria-label="Close"
        >
          ×
        </button>
        <div style={{ fontWeight: 600, marginBottom: '6px', opacity: 0.9, paddingRight: '14px' }}>Reactions</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {visible.map((liker, idx) => (
            <div
              key={idx}
              style={{
                padding: '2px 0',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <ReactionIcon type={liker.reaction_type || DEFAULT_REACTION} size={12} />
              <span style={{ fontWeight: 500 }}>{getLikerDisplayName(liker)}</span>
            </div>
          ))}
          {hasMore && (
            <div style={{ paddingTop: '4px', fontWeight: 500 }}>
              and {remaining} more...
            </div>
          )}
        </div>
      </div>
    </div>
  );
})()}

      <RepostModal
        open={!!repostModalPost}
        post={repostModalPost}
        user={user}
        initialCaption={
          repostModalPost
            ? userCaptions[String(getRepostTargetId(repostModalPost))] || ''
            : ''
        }
        alreadyReposted={
          !!repostModalPost &&
          !!userReposted[String(getRepostTargetId(repostModalPost))]
        }
        submitting={repostSubmitting}
        onClose={() => {
          if (!repostSubmitting) setRepostModalPost(null);
        }}
        onSubmit={submitRepost}
        onUnrepost={unrepostFromModal}
      />

    </div>
  );
}












export default PostList;